import WebApp from '@twa-dev/sdk';
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import md5 from 'md5';

import {
  EXCLUSIVE_MARKET_SOURCE,
  LIGHT_MARKET_SOURCE,
} from '@/app/lib/localization/constants';

import { isTMA } from '@/shared/helpers/telegram/is-tma';
import { returnToMarket } from '@/shared/helpers/telegram/return-to-market';
import { showPopup } from '@/shared/helpers/telegram/show-popup';
import { useAppStateRaw } from '@/shared/stores/app-store/app-store';

import { getIsDev } from '../helpers/environment/get-is-dev';
import { getDecodedStartappQuery } from '../helpers/telegram/get-decoded-startapp-query';
import { parseInitDataForWeb } from '../helpers/telegram/parse-init-data';
import { resolvePdTma } from '../helpers/telegram/resolve-pd-tma';
import { ApiLogger } from '../service/logger/ApiLogger';
import { ErrorLogger } from '../service/logger/ErrorLogger';

const WEB_SOURCE = 'pd_web';

export class ApiService {
  static readonly instance: AxiosInstance = axios.create({
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });
  /** Standalone-бот (то же значение, что query/startapp `pdTma`); бэкенд читает контекст отсюда, без дубля в теле запросов. */
  static readonly PD_TMA_HEADER_NAME = 'X-Pd-Tma';
  private static readonly LOCALE_HEADER_NAME = 'X-Locale';
  private static readonly RETRY_ATTEMPTS_COUNT = 5;
  private static readonly SEC_HEADER_NAME = 'Security-Ch-Ua-SigHash';

  public static async init() {
    const baseUrl = await ApiService.getBaseUrl();
    this.instance.defaults.baseURL = baseUrl;

    const token = await ApiService.getToken();
    this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    this.instance.interceptors.request.use(this.addSecHeader.bind(this));
    this.instance.interceptors.response.use(
      ApiLogger.apiSuccessLogger.bind(ApiLogger),
      ApiLogger.apiErrorLogger.bind(ApiLogger)
    );

    axiosRetry(this.instance, {
      retries: this.RETRY_ATTEMPTS_COUNT,
      retryDelay: (...arg) => exponentialDelay(...arg, 1000),
      retryCondition(error) {
        if (error.code === 'ERR_NETWORK') return true;
        if (error.code === 'ECONNABORTED') return true;

        switch (error.response?.status) {
          // retry only if status is 500 or 501
          case 500:
          case 501:
            return true;
          default:
            return false;
        }
      },
      onRetry: (retryCount, error) => {
        if (import.meta.env.DEV) ApiLogger.retryInDevMode(error, retryCount);
      },

      onMaxRetryTimesExceeded: (error) => {
        if (import.meta.env.DEV) ApiLogger.apiErrorLogger(error);
      },
    });

    return token;
  }

  private static addSecHeader(request: InternalAxiosRequestConfig) {
    const { method, url } = request;

    let contentLen = 0;
    const requestMethod = String(method).toUpperCase();

    if (request.data) {
      contentLen = JSON.stringify(request.data).length;
    }

    const path = url![0] === '/' ? url : `/${url}`;
    const hashString = `${requestMethod}+/api${path}+${contentLen}`;
    const hash = md5(hashString);

    request.headers.set(this.SEC_HEADER_NAME, hash);
    request.headers.set(this.LOCALE_HEADER_NAME, useAppStateRaw.getState().lang.app);

    const pdTma = resolvePdTma();
    if (pdTma) {
      request.headers.set(this.PD_TMA_HEADER_NAME, pdTma);
    }

    const token = useAppStateRaw.getState().token;

    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }

    return request;
  }

  private static async getBaseUrl() {
    const startappQuery = getDecodedStartappQuery();

    let domain: string;

    switch (startappQuery?.src) {
      case LIGHT_MARKET_SOURCE:
        domain = import.meta.env.VITE_APP_API_URL_LITE;
        break;
      case EXCLUSIVE_MARKET_SOURCE:
        domain = import.meta.env.VITE_APP_API_URL_EXCLUSIVE;
        break;
      default:
        domain = import.meta.env.VITE_APP_API_URL;
    }
    const baseUrl = `${domain}/api`;

    return baseUrl;
  }

  private static async getToken(): Promise<string> {
    const DEV_TOKEN = import.meta.env.VITE_APP_DEV_TOKEN;

    if (import.meta.env.DEV && DEV_TOKEN) {
      return DEV_TOKEN;
    }

    try {
      const startappQuery = getDecodedStartappQuery();

      const initData = this.retrieveInitData();
      const isWebMode = startappQuery.src === WEB_SOURCE;

      useAppStateRaw.getState().actions.setIsWebMode(isWebMode);

      let modifiedInitData = '';

      if (startappQuery.src && !isWebMode) {
        modifiedInitData = `${initData}&src=${startappQuery.src}`;
      } else {
        modifiedInitData = initData;
      }

      const encodedInitData = encodeURIComponent(modifiedInitData);

      let url,
        requestBody = {};

      const gameKey =
        startappQuery?.game?.key ??
        WebApp.initDataUnsafe.start_param ??
        import.meta.env.VITE_DEV_FALLBACK_GAME_KEY;

      if (isWebMode) {
        url = `game/v1/tg/token/${gameKey}`;
        requestBody = parseInitDataForWeb(modifiedInitData);
      } else {
        url = `game/v1/token/${gameKey}?initData=${encodedInitData}`;
      }

      const response = await this.instance.post(url, requestBody);

      const token = response.data?.token;

      if (!token) throw new Error('No token found');

      return token;
    } catch (error) {
      ErrorLogger.error(error as Error, 'getToken error');

      const isDev = getIsDev('both');

      if (!isDev) {
        await this.showTokenErrorAndReturn(error);
      }
      throw error;
    }
  }

  private static retrieveInitData() {
    const hashBasedInitData = new URLSearchParams(location.hash.slice(1)).get(
      'tgWebAppData'
    );
    const initData = WebApp?.initData || hashBasedInitData;

    if (!initData) {
      throw new Error('No init data found');
    }

    return initData;
  }

  private static async showTokenErrorAndReturn(error: unknown): Promise<void> {
    const message = error instanceof Error ? error.message : 'Failed to get token';

    if (isTMA()) {
      await new Promise<void>((resolve) => {
        showPopup(
          {
            title: 'Error',
            message,
            buttons: [{ id: 'ok', type: 'default', text: 'OK' }],
          },
          () => resolve()
        );
      });
    } else {
      window.alert(message);
    }

    await returnToMarket();
  }
}
